import { useEffect, useMemo, useState, useCallback, useRef } from "react"
import { useParams, useSearchParams, useNavigate } from "react-router-dom"
import { PreJoinPage } from "@/features/prejoin"
import { MeetingScreen } from "@/features/meeting"
import { useMeetingsStore } from "@/features/meetings/store/meetings.store"
import { selectIsAuthenticated, useAuthStore } from "@/features/auth/store/auth.store"
import { meetingsService } from "@/features/meetings/services/meetings.service"
import { meetingsApi, guestApi, ApiError, type WaitingFor } from "@/features/meetings/services/meetings.api"
import { markGuestSession } from "@/features/auth/services/guest-session"
import { MeetingPasswordDialog } from "@vc/views/dialogs/MeetingPasswordDialog"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useWebinarStore } from "@/features/webinar/store/webinar.store"
import { useDevicesStore } from "@/features/devices"
import { useMeetingSessionStore } from "@/features/meeting/store/meeting-session.store"
import { useMeetingPreferencesStore } from "@/features/meeting/store/meeting-preferences.store"
import { useParticipantVolumeStore } from "@/features/meeting/store/participant-volume.store"
import { useParticipantVideoHiddenStore } from "@/features/meeting/store/participant-video-hidden.store"
import { WaitingRoom } from "@vc/views/extraFiles/WaitingRoom"
import { AccessCode } from "@vc/views/extraFiles/AccessCode"
import { UnsupportedBrowser } from "@vc/views/extraFiles/UnsupportedBrowser"
import { MeetingEnded } from "@vc/views/extraFiles/MeetingEnded"
import { WebinarRegistration } from "@vc/pages/WebinarRegistration"
import { WaitingLobby } from "@vc/pages/WaitingLobby"
import { useVisualViewport } from "@vc/viewmodels/hooks/useVisualViewport"
import { MediaStreamProvider } from "@vc/contexts/MediaStreamContext"
import { deviceManager, useDeviceIssues } from "@/features/devices"
import type { AppScreen, MeetingInfo } from "@vc/models"
import { applyPreferredSpeaker } from "@/features/devices/services/audio-output.service"
import { JOIN_KEY_PARAM, ROLE_PARAM, PASSIVE_ROLE } from "@/features/meetings/utils/joinLinks"
import { guestPasswordFor, clearGuestPassword, rememberGuestPassword } from "@/features/meetings/services/guest-password"
import { wasMeetingFull, clearMeetingFull } from "@/features/meetings/services/meeting-full-notice"
import MeetingFullDialog from "@/features/meetings/components/MeetingFullDialog"
import { APP_ROUTES, AUTH_ROUTES } from "@/shared/constants/routes.constants"
import { emitToParent, isEmbedded, onParentCommand } from "@/features/embed/embed-bridge"
import { exitMeetingRoute } from "@/features/embed/meeting-exit"

function detectSupport(): { supported: boolean; reason: "browser" | "webrtc" | null } {
  if (typeof window === "undefined") return { supported: false, reason: "browser" }
  if (!navigator.mediaDevices || !window.RTCPeerConnection) {
    return { supported: false, reason: "webrtc" }
  }
  return { supported: true, reason: null }
}

const JOIN_SOUND_SRC = "/audios/mixkit-alert-bells-echo-765join.wav"
const LEAVE_SOUND_SRC = "/audios/mixkit-doorbell-tone-2864end.wav"
const JOIN_SOUND_DELAY_MS = 120
const LEAVE_TRANSITION_DELAY_MS = 160
/** A password the server refused: the same words for a guest and a signed-in join. */
const WRONG_PASSWORD_MESSAGE = "That password is not correct. Check it and try again."

export default function VcApp() {
  useVisualViewport()

  const { meetingId } = useParams<{ meetingId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const meetingCode = meetingId || ""
  // Anyone without a session on a /meeting/:id link is a guest — the shared
  // link carries no role flag, so identity is what decides. ?role=guest is
  // still honoured for links handed out before that changed.
  //
  // Use the store's OWN predicate, the one ProtectedRoute gates on. A truthy
  // `user` is not the same thing: it is hydrated from localStorage and outlives
  // the session, so `!user` sent signed-in people down the guest branch, where
  // seedGuestMeeting replaces the real record with a stub and the panel loses
  // the host, the schedule and the readable id.
  const authUser = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  // Guest-ness is having no session, full stop. `?role=guest` used to force it
  // too, back when a separate guest link existed; that link is gone, and a flag
  // in a URL is the wrong place for this anyway — it survives being pasted
  // around, so a signed-in person opening someone else's copied link was
  // demoted to a guest and made to retype an identity we already held.
  const isGuest = !isAuthenticated

  // Name a guest typed in prejoin. The direct guest URL carries no ?name=, so we
  // capture it here (rather than mutating the URL, which would re-run
  // MeetingGuard and remount VcApp) and let it override the default. A signed-in
  // user is already identified, so their own name wins over any ?name= in a
  // link someone else built.
  const [joinedName, setJoinedName] = useState<string | null>(null)
  // A guest must introduce themselves. `authUser` is hydrated from localStorage
  // and outlives the session, so falling back to it prefilled the box with
  // whoever last signed in on this browser — a guest would arrive already
  // wearing someone else's name and could join without ever noticing.
  // `?name=` is read ONCE, on the first render, and then removed from the
  // address bar — a name in a URL is noise the user did not put there, and it
  // travels on if they copy the link out of the bar to send to someone else,
  // handing that person a stranger's name in their own prejoin box.
  //
  // Captured into a ref rather than read inline, because stripping the param
  // would otherwise blank the very value it just supplied. The link still works
  // without it: a signed-in user falls back to their account name, and a guest
  // to the prejoin field they have to fill in anyway.
  const nameFromLink = useRef(searchParams.get("name") ?? "")
  useEffect(() => {
    if (!searchParams.has("name")) return
    const next = new URLSearchParams(searchParams)
    next.delete("name")
    // Through the router, not `history.replaceState`, so react-router's own
    // idea of the location matches the bar. `replace` keeps the back button
    // pointing where the user came from rather than at a URL they never chose.
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const userName = isGuest
    ? joinedName || nameFromLink.current || ""
    : joinedName || authUser?.name || nameFromLink.current || "Guest"

  // A protected meeting refuses the first join, which is a challenge rather
  // than a failure: hold the attempt, ask for the password, and replay it.
  // `pendingJoinName` is what makes the retry possible — the name was typed in
  // prejoin and the dialog does not know it.
  // Whether this meeting is protected, straight from the preview API. Known
  // before anyone clicks Join, so the dialog is shown only for a meeting that
  // actually has a password rather than to everyone who tries.
  const [passwordRequired, setPasswordRequired] = useState(false)
  const [passwordPromptOpen, setPasswordPromptOpen] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [pendingJoinName, setPendingJoinName] = useState<string | null>(null)

  useEffect(() => {
    if (!meetingCode) return
    let cancelled = false
    void guestApi
      .getMeetingPreview(meetingCode)
      .then(({ meeting }) => {
        if (!cancelled) setPasswordRequired(Boolean(meeting?.passwordRequired))
      })
      .catch(() => {
        // Unreadable preview is not a reason to block: the join itself still
        // refuses without a password, and that refusal opens the dialog.
      })
    return () => {
      cancelled = true
    }
  }, [meetingCode])

  // Lobby request id while a guest waits for host admission (waiting-room poll).
  const [waitingRequestId, setWaitingRequestId] = useState<string | null>(null)
  // Why they are waiting, which decides what the waiting screen says: the host
  // has not arrived yet, or the host has to let them in.
  const [waitingFor, setWaitingFor] = useState<WaitingFor>("admission")

  // The "meeting is full" dialog. Open from the first paint when the guard's
  // check on opening the link already found every seat taken.
  const [meetingFull, setMeetingFull] = useState(() => wasMeetingFull(meetingCode))
  const [fullRetrying, setFullRetrying] = useState(false)
  const [stillFull, setStillFull] = useState(false)
  // Closed with its X: they stay on the preview, with Join disabled.
  const [fullDismissed, setFullDismissed] = useState(false)
  // Set by every MEETING_FULL, so a Try again can tell it was refused again.
  const fullAgainRef = useRef(false)
  // What the last Join click sent, for Try again to send again.
  const lastJoinRef = useRef<{ name: string; code: string } | null>(null)

  useEffect(() => {
    clearMeetingFull()
  }, [])

  // Classify the visitor from the URL before anything asks for a device.
  //
  // The join call settles this properly, but it happens when they press Join —
  // long after the prejoin preview has opened the camera. A passive attendee has
  // no camera in this meeting, so prompting them for one is asking permission to
  // use something that will never be used, and browsers remember the refusal.
  // Set here, on the marker alone; the server's verdict still overrides it at
  // join time.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get(ROLE_PARAM) === PASSIVE_ROLE) {
      useMeetingSessionStore.getState().setViewOnly(true)
    }
  }, [])

  // Source of truth for the meeting comes from the persisted store. The
  // route guard has already verified existence + password before we mount.
  const meetings = useMeetingsStore((s) => s.meetings)
  const webinars = useWebinarStore((s) => s.webinars)
  const meetingRecord = useMemo(
    () => meetings.find((m) => m.id === meetingCode || m.meetingId === meetingCode),
    [meetings, meetingCode],
  )
  const webinarRecord = useMemo(
    () => webinars.find((w) => w.id === meetingCode),
    [webinars, meetingCode],
  )
  const isWebinar = !!webinarRecord && !meetingRecord

  // The meetings store hydrates from localStorage and is only ever filled from
  // the API by the dashboard, the calendar and the create dialogs. Nothing on
  // `/meeting/:id` loads it — so arriving by a shared link (the normal way in)
  // left the store empty, and the info panel fell back to the URL uuid with no
  // host, schedule or duration. Fetch the one record we need. Guests are
  // already covered by seedGuestMeeting; they cannot call this authed endpoint.
  const seededCodeRef = useRef<string | null>(null)
  useEffect(() => {
    if (!meetingCode || isGuest || meetingRecord) return
    if (seededCodeRef.current === meetingCode) return
    seededCodeRef.current = meetingCode

    let cancelled = false
    void (async () => {
      try {
        const { meeting } = await meetingsApi.getById(meetingCode)
        if (cancelled || !meeting) return
        useMeetingsStore.getState().addMeeting(meeting)
      } catch {
        // Non-fatal: the panel keeps its fallbacks rather than blocking the join.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [meetingCode, isGuest, meetingRecord])

  // Splash is owned by `MeetingGuard` (runs before VcApp mounts) so VcApp no
  // longer maintains its own boot-loading screen.
  const [currentView, setCurrentView] = useState<"dashboard" | "webinar" | "lobby">("dashboard")
  const [screen, setScreen] = useState<AppScreen>(() => {
    const { supported } = detectSupport()
    return supported ? "dashboard" : "unsupported"
  })
  const meetingInfo = useMemo<MeetingInfo>(
    () => ({
      userName,
      meetingCode,
      meetingName: meetingRecord?.title ?? webinarRecord?.title ?? "Meeting",
    }),
    [userName, meetingCode, meetingRecord?.title, webinarRecord?.title],
  )

  // (Media-entry settings — muteOnEntry / videoOnEntry — are applied by
  // MeetingGuard *before* this component mounts so the prejoin preview shows
  // the host's chosen defaults on first paint.)

  // Record this participant in the meeting record's participants[] (host has
  // already been added at create time; non-hosts get appended on join).
  useEffect(() => {
    if (!meetingRecord || screen !== "meeting") return
    // The readable code, not the uuid: `join` re-runs `validateJoin`, which
    // asserts the id is 9 digits, so passing `record.id` threw every time —
    // silently, because the catch below swallows it, so no participant was
    // ever recorded on join.
    const joinId = meetingRecord.meetingId ?? meetingRecord.id
    void meetingsService.join({ meetingId: joinId, name: userName }).catch(() => {
      // Validation/auth errors are handled by the guard; here we only care
      // about recording the join. Failures are non-fatal.
    })
  }, [meetingRecord?.id, screen, userName])
  const [unsupportedReason] = useState<"browser" | "webrtc">(() => {
    const { reason } = detectSupport()
    return reason ?? "webrtc"
  })
  const [endedReason, setEndedReason] = useState<"ended" | "expired" | "removed" | "left">("left")
  const leaveTransitionTimerRef = useRef<number | null>(null)
  /** Which devices this session is holding open across the join, if any. */
  const deviceClaimRef = useRef<{ audio: boolean; video: boolean }>({ audio: false, video: false })
  /**
   * Mic and camera as they were when this visit ended, for "Try to rejoin".
   *
   * The join copies the prejoin choice (`useDevicesStore`) over the meeting's,
   * and the controls in the meeting never write back to it — so someone who
   * joined unmuted, muted, and was removed came back unmuted. A rejoin shows
   * no preview to correct that on, so it carries the state they left with.
   * `null` when there is nothing to carry: a view-only visit forced both off,
   * which was the room's decision rather than theirs.
   */
  const lastMediaRef = useRef<{ micOn: boolean; camOn: boolean } | null>(null)

  // Device problems reach the user here rather than in whichever panel was
  // open when they happened.
  useDeviceIssues()

  /**
   * Close the camera and microphone on the way out.
   *
   * Stopped outright rather than released. `release` is cooperative — it
   * decrements a refcount and trusts that every claim taken anywhere was also
   * given back — and a single unbalanced one keeps the count above zero, so the
   * device is never retired and the browser goes on showing the camera-in-use
   * dot on a tab whose meeting has ended. The user cannot clear that without
   * closing the tab, and has no reason to believe they are not still watched.
   */
  // Temporary: `inmeetDevices()` in the console reports what the device manager
  // still holds. Paired with the [devices] lines, it separates "we did not stop
  // it" from "something outside this module is holding the camera".
  useEffect(() => {
    const w = window as unknown as { inmeetDevices?: () => unknown }
    w.inmeetDevices = () => deviceManager.debugState()
    return () => {
      delete w.inmeetDevices
    }
  }, [])

  /**
   * The session's camera claim follows the camera, rather than the meeting.
   *
   * EXPERIMENT (camera close-on-off). The claim taken at `enterMeeting` exists
   * to bridge the join — the lobby preview lets go the moment the screen
   * changes and the publisher does not take hold until transports are up, and
   * without something spanning that gap the camera closes and re-opens on every
   * join. It was then held for the whole meeting, which was free while camera-off
   * meant "paused".
   *
   * It is not free now. `deviceManager.stop('video')` closes the hardware past
   * any outstanding claim, so the light does go out — but it leaves the slot
   * EMPTY WITH CLAIMS STILL ON IT, and that is a state the manager reads as "a
   * device died under a holder". Both `setDevice`'s repair branch and
   * `recoverAfterLoss` are entitled to fix it by opening the camera again. A
   * claim nobody needs is therefore a standing invitation to turn the camera
   * back on behind the user.
   *
   * So it is given back when the camera goes off. Not re-taken when it comes
   * on: the gap it was written for is the join, and that is long past.
   */
  const camOn = useMeetingSessionStore((s) => s.camOn)
  useEffect(() => {
    if (camOn || !deviceClaimRef.current.video) return
    deviceClaimRef.current.video = false
    deviceManager.release('video')
  }, [camOn])

  const releaseDeviceClaim = useCallback(() => {
    console.info('[devices] leaving — closing camera and microphone')
    // Give back the claim `enterMeeting` took, THEN force the close. `stopAll`
    // keeps refcounts on purpose (every holder still owes its release), so a
    // claim this component never returns is never returned by anyone: the slot
    // sits empty with a claim on it for the rest of the tab, which the manager
    // reads as "a device died under a holder". From then on a device change
    // reopens the camera via `setDevice`'s repair, a late `prime` is never
    // retired, and the next prejoin preview never closes — the indicator the
    // original fix was for, back after the first meeting.
    const claim = deviceClaimRef.current
    deviceClaimRef.current = { audio: false, video: false }
    if (claim.audio) deviceManager.release('audio')
    if (claim.video) deviceManager.release('video')
    deviceManager.stopAll()
  }, [])

  /**
   * Close the camera and microphone once the meeting screen has gone.
   *
   * Keyed on the screen rather than done in the leave handler, and that timing
   * IS the fix. Leaving keeps the meeting mounted for the exit animation, and
   * every video tile still on screen reaches for the camera through
   * `getLocalStream()` — so a stop issued at the click is undone by a tile
   * re-acquiring a frame later, and the hardware light never goes out. By the
   * time this runs the meeting has unmounted and nothing is left to ask.
   */
  const wasInMeetingRef = useRef(false)
  useEffect(() => {
    if (screen === "meeting") {
      wasInMeetingRef.current = true
      return
    }
    // Only on the way OUT of a meeting. Firing whenever the screen is not a
    // meeting would close the camera the PREJOIN preview has open — the lobby
    // is a non-meeting screen too, and its whole job is showing you yourself.
    if (!wasInMeetingRef.current) return
    wasInMeetingRef.current = false
    releaseDeviceClaim()
  }, [screen, releaseDeviceClaim])

  const playUiSound = useCallback((src: string, volume = 0.85) => {
    try {
      const audio = new Audio(src)
      audio.preload = "auto"
      audio.volume = volume
      // Meeting UI sounds follow the chosen output, like the voices do.
      applyPreferredSpeaker(audio)
      void audio.play().catch(() => {
        // Ignore autoplay or playback failures
      })
    } catch {
      // Ignore unsupported audio errors
    }
  }, [])

  // Leaving the meeting route by ANY means — the Leave button, the browser back
  // button, a link to the dashboard — drops both backgrounds and the session's
  // device claim. handleLeaveMeeting covers the deliberate exit; this catches
  // the rest, so neither a background nor an open camera can outlive the
  // session that started it.
  useEffect(() => {
    return () => {
      useMeetingPreferencesStore.getState().clearBackgrounds()
      // Per meeting, like the backgrounds: the next one starts with your tile shown.
      useMeetingPreferencesStore.getState().setHiddenSelfVideo(false)
      releaseDeviceClaim()
    }
  }, [releaseDeviceClaim])

  /**
   * Every seat is taken (MEETING_FULL). Said in a dialog over the preview, and
   * they stay on it: a seat can free up at any moment, and Try again from here
   * beats finding the link again. The dialog's other button is the way out.
   * This used to toast and send them to the dashboard, or a guest to sign-in.
   */
  const showMeetingFull = useCallback(() => {
    fullAgainRef.current = true
    setWaitingRequestId(null)
    // The waiting room is over; back to the preview.
    setScreen((s) => (s === "waiting-room" ? "dashboard" : s))
    setStillFull(false)
    setMeetingFull(true)
  }, [])

  const enterMeetingWithJoinSound = useCallback(async () => {
    const devices = useDevicesStore.getState()
    const session = useMeetingSessionStore.getState()
    /** Set only when the server itself classified this join. */
    let serverMode: "active" | "passive" | undefined
    // Carried across as a STATE, not as a pair of toggles. `micEnabled` has
    // already been through the permission gate in the lobby, so a device the
    // browser refused arrives here as `false` and the meeting starts with the
    // control visibly off — and forcing the value cannot land the wrong way
    // round if something changed between the read and the flip.
    if (session.micOn !== devices.micEnabled) session.setMicOn(devices.micEnabled)
    if (session.camOn !== devices.camEnabled) session.setCamOn(devices.camEnabled)

    // Fetch real room credentials if not already set (e.g. user came from the
    // meetings list or typed the URL directly, bypassing the instant-meeting dialog).
    // Guests are skipped here — their credentials come from the guest-admission
    // flow (guestJoinOpen / lobby poll) which runs before this is called.
    if (!session.roomCredentials && meetingCode && !isGuest) {
      try {
        // The join key rides in the URL of whichever link they opened. The
        // server decides what it means; we only carry it.
        const joinKey = new URLSearchParams(window.location.search).get(JOIN_KEY_PARAM)
        // The password the guard verified before the preview. The guard only
        // checks, it no longer joins, so this is the join that needs it.
        const password = guestPasswordFor(meetingCode)
        const data = await meetingsApi.join(meetingCode, {
          ...(joinKey ? { key: joinKey } : {}),
          ...(password ? { password } : {}),
        })
        // A locked room holds signed-in joiners too, not just guests — the
        // server answers `waiting` with a request id instead of credentials.
        // Divert to the same waiting room + poll the guest flow already uses.
        if (data.status === "waiting" && data.requestId) {
          setWaitingRequestId(data.requestId)
          setWaitingFor(data.waitingFor ?? "admission")
          setScreen("waiting-room")
          return false
        }
        session.setRoomCredentials(data.roomCredentials)
        // The server read a key we cannot forge, so its verdict is the real
        // one. Recorded here; the marker below only speaks when it stays silent.
        if (data.mode) serverMode = data.mode
      } catch (err: any) {
        if (err?.code === "MEETING_FULL") {
          showMeetingFull()
          return false
        }
        // A protected meeting asks for the password; it does not give up.
        // Leaving clears it (handleLeaveMeeting), so "Try to Rejoin" always
        // arrives here without one, and this toast left a signed-in participant
        // with no way to type it: the prompt only ever opened for guests. A
        // password we did send was wrong, so the prompt says so and forgets it —
        // which is also how handlePasswordSubmit tells that outcome apart.
        if (err?.code === "INVALID_PASSWORD") {
          const sent = guestPasswordFor(meetingCode)
          clearGuestPassword()
          setPasswordError(sent ? WRONG_PASSWORD_MESSAGE : null)
          setPasswordPromptOpen(true)
          return false
        }
        // Fatal for a real meeting. This used to fall through and open the
        // meeting screen anyway, on the reasoning that a missing SFU is
        // survivable — but a meeting with no credentials is not a quiet
        // degradation, it is a room the server has never heard of: no roster,
        // no media, and nothing the user does inside it reaches anyone.
        // Failing here keeps them on a screen that still has a way forward.
        toast.error({
          title: "Couldn't rejoin",
          description: err?.message || "The meeting could not be reached. Please try again.",
        })
        return false
      }
    }

    // A real meeting needs credentials to be a meeting at all. Guests reach this
    // with theirs already set by the admission flow; anyone arriving without
    // them has had a join fail silently somewhere upstream, and opening the
    // meeting screen regardless is what produced a room full of people who were
    // not there. No credentials, no entry.
    if (meetingCode && !useMeetingSessionStore.getState().roomCredentials) {
      toast.error({
        title: "Couldn't rejoin",
        description: "This meeting is no longer reachable. Try joining again from the dashboard.",
      })
      return false
    }

    // View-only, decided on every way into the meeting rather than inside the
    // credential fetch above — a guest, or anyone whose credentials were already
    // set by the admission flow, never runs that branch and would otherwise walk
    // in through the passive link with a full control bar.
    //
    // The server's classification wins wherever it gave one, because it read a
    // key the client cannot forge. The URL marker is the fallback for meetings
    // that have no keys yet, and it is exactly that: a label this client chooses
    // to honour, not a restriction it is held to.
    const markedPassive =
      new URLSearchParams(window.location.search).get(ROLE_PARAM) === PASSIVE_ROLE
    session.setViewOnly(serverMode === "passive" || (!serverMode && markedPassive))
    // A meeting never opens with incoming audio paused. The leave button clears
    // the flag, but Back or a route change never reach it — and a view-only join
    // has no menu to resume from. Breakout moves swap credentials without coming
    // through here, so a pause still follows the user into a breakout.
    useMeetingSessionStore.setState({ areIncomingAudiosPaused: false })

    // Carry the lobby's background into the meeting. One copy, at the single
    // point where the meeting actually starts — after this the two values are
    // independent, so switching background mid-call never rewrites the lobby.
    useMeetingPreferencesStore.getState().applyLobbyBackgroundToMeeting()

    // Hold the lobby's devices across the join.
    //
    // The prejoin preview lets go the moment this screen changes, and the
    // publisher does not take hold until transports are up — a gap of however
    // long the SFU handshake takes. Without a claim spanning it the camera
    // would close and re-open on every join, which is the blink this ownership
    // model exists to remove. `retain` holds only what is already open, so
    // joining with the camera off does not switch it on.
    deviceClaimRef.current = {
      audio: deviceManager.retain('audio'),
      video: deviceManager.retain('video'),
    }

    setScreen("meeting")
    window.setTimeout(() => {
      playUiSound(JOIN_SOUND_SRC, 0.82)
    }, JOIN_SOUND_DELAY_MS)
    return true
  }, [playUiSound, meetingCode, isGuest, showMeetingFull])

  useEffect(() => {
    return () => {
      if (leaveTransitionTimerRef.current) {
        window.clearTimeout(leaveTransitionTimerRef.current)
      }
    }
  }, [])

  // Seed the meetings store from a guest-preview meeting so the meeting title
  // and settings are available to VcApp / MeetingScreen without an auth fetch.
  const seedGuestMeeting = useCallback((meeting: any) => {
    // The guest payload is snake_case and carries only id/title/host_name.
    // Writing it through verbatim produced a record whose fields the meeting
    // UI does not read (`host_name` vs `host`, `duration_minutes` vs
    // `durationMinutes`), so the info panel rendered an empty shell. Map it.
    // Seeded, not added: a guest's preview must not reach the stored list the
    // dashboard reads after the next sign-in (see `seedMeeting`).
    useMeetingsStore.getState().seedMeeting({
      id: meeting.id,
      // Only set when the server actually gives us one — faking it from the
      // uuid is what put a 36-character string in the "Meeting ID" row.
      meetingId: meeting.meetingId ?? meeting.meeting_id,
      title: meeting.title,
      host: meeting.host_name ?? meeting.host,
      status: "active",
      type: "joined",
      settings: meeting.settings ?? {},
      durationMinutes: meeting.duration_minutes ?? meeting.durationMinutes,
      recurring: false,
      seats: 100,
    } as any)
  }, [])

  // A guest has been admitted (immediately or after the lobby): seed the store,
  // mark this tab as a guest (opts out of authed-only calls), and stash the SFU
  // room credentials for useMeetingConnection.
  const admitGuest = useCallback((meeting: any, roomCredentials: any) => {
    seedGuestMeeting(meeting)
    markGuestSession()
    useMeetingSessionStore.getState().setRoomCredentials(roomCredentials)
  }, [seedGuestMeeting])

  /**
   * The host said no — stop waiting and say so.
   *
   * Shared by the explicit `denied` answer and by the request having gone,
   * because they are the same thing to the person waiting: nobody is coming to
   * let them in. The difference only matters to the server.
   */
  const declineEntry = useCallback(() => {
    setWaitingRequestId(null)
    toast.error({
      title: "Join request declined",
      description: "The host declined your request to join this meeting.",
    })
    setScreen("dashboard")
  }, [])

  // While a guest waits for host admission, poll the lobby until admitted/denied.
  useEffect(() => {
    if (!waitingRequestId) return
    const interval = window.setInterval(async () => {
      try {
        const result = await guestApi.lobbyStatus(waitingRequestId)
        if (result.status === "admitted") {
          window.clearInterval(interval)
          setWaitingRequestId(null)
          // Only an actual guest. This used to run for whoever came out of the
          // waiting room, which marked a SIGNED-IN participant as a guest for
          // the rest of the session — opting them out of authed calls and
          // falling their name back to the guest branch. A locked meeting could
          // already route a signed-in user through here; holding them until the
          // host arrives makes it the ordinary path rather than a corner.
          if (isGuest) markGuestSession()
          useMeetingSessionStore.getState().setRoomCredentials(result.roomCredentials)
          void enterMeetingWithJoinSound()
        } else if (result.status === "denied") {
          window.clearInterval(interval)
          declineEntry()
        } else if (result.status === "full") {
          // Let in, but the room filled while they waited. Nobody is going to
          // free a seat for them, so stop waiting and say why.
          window.clearInterval(interval)
          showMeetingFull()
        }
      } catch (err) {
        // A request that is GONE is not a blip. The guest endpoint answers 404
        // once the request has been decided and dropped, or once the room
        // process that held it has gone — and both mean nobody is ever going
        // to admit this person. Treating that as a transient failure, which is
        // what catching everything did, left a rejected guest watching a
        // spinner for the rest of the meeting.
        //
        // Anything else really is a blip: keep polling.
        if (err instanceof ApiError && err.status === 404) {
          window.clearInterval(interval)
          declineEntry()
        }
      }
    }, 2000)
    return () => window.clearInterval(interval)
  }, [waitingRequestId, isGuest, enterMeetingWithJoinSound, declineEntry, showMeetingFull])

  const handleJoinMeeting = useCallback(async (finalUserName: string, finalMeetingCode: string) => {
    lastJoinRef.current = { name: finalUserName, code: finalMeetingCode }
    // If the meeting code indicates waiting room or access code
    if (finalMeetingCode.startsWith("WAIT")) {
      setScreen("waiting-room")
      return
    }
    if (finalMeetingCode.startsWith("LOCK")) {
      setScreen("access-code")
      return
    }

    // Direct guest join (/meeting/:id?role=guest): call the guest-admission API
    // on this Join click — the work the old /join page used to do. Immediate
    // admit → enter; host-gated → waiting room + lobby poll (effect above).
    const session = useMeetingSessionStore.getState()
    if (isGuest && !session.roomCredentials) {
      // Remember the typed name so meetingInfo.userName / the participant record
      // use it instead of the "Guest" default.
      setJoinedName(finalUserName)

      // Already answered at the door, before the preview. MeetingGuard asks a
      // guest for the password up front and verifies it, so asking again here
      // would be a second prompt for a question they have already got right.
      const verified = guestPasswordFor(meetingCode)

      // Protected meeting: ask first. The join below is still the authority —
      // it refuses without a password either way — but there is no sense
      // spending a round trip to be told what the preview already said.
      if (passwordRequired && !verified) {
        setPendingJoinName(finalUserName)
        setPasswordError(null)
        setPasswordPromptOpen(true)
        return
      }

      try {
        const result = await guestApi.guestJoinOpen(
          meetingCode,
          finalUserName,
          verified ?? undefined,
        )
        if (result.status === "waiting") {
          seedGuestMeeting(result.meeting)
          setWaitingRequestId(result.requestId)
          setWaitingFor(result.waitingFor ?? "admission")
          setScreen("waiting-room")
          return
        }
        admitGuest(result.meeting, result.roomCredentials)
      } catch (err: any) {
        // Protected meeting: ask, do not give up. The join resumes in
        // handlePasswordSubmit with the same name.
        if (err?.code === "INVALID_PASSWORD") {
          setPendingJoinName(finalUserName)
          setPasswordError(null)
          setPasswordPromptOpen(true)
          return
        }
        if (err?.code === "MEETING_FULL") {
          showMeetingFull()
          return
        }
        toast.error({
          title: "Couldn't join",
          description: err?.message || "Failed to join meeting",
        })
        return
      }
    }

    // Awaited so a Try again from the "meeting is full" dialog knows how it went.
    await enterMeetingWithJoinSound()
  }, [enterMeetingWithJoinSound, isGuest, meetingCode, passwordRequired, seedGuestMeeting, admitGuest, showMeetingFull])

  // `ended` when the host closed the room for everyone, `left` when you walked
  // out yourself — the two read very differently on the ended screen, and
  // telling someone "You've Left" after the host ended the call is misleading.
  /**
   * Replay the held join with the password the participant typed.
   *
   * Verified by the API, never here — the client has no business knowing the
   * meeting's password, and a check in the browser is a check anyone can skip.
   * Wrong password keeps the dialog open with the reason; right password
   * carries on into the room exactly as an unprotected join would.
   */
  const handlePasswordSubmit = useCallback(
    async (password: string) => {
      const name = pendingJoinName || userName
      setPasswordError(null)
      // Signed in: not a guest admission. It is the same join the Join click
      // makes (enterMeetingWithJoinSound reads the password from here), and
      // only that join can tell a wrong password — which it reports by keeping
      // this dialog open with the reason and forgetting the password. Anything
      // else ends the question: in, waiting room, or an error it shows itself.
      if (!isGuest) {
        rememberGuestPassword(meetingCode, password)
        const entered = await enterMeetingWithJoinSound()
        if (entered || guestPasswordFor(meetingCode) !== null) {
          setPasswordPromptOpen(false)
          setPendingJoinName(null)
        }
        return
      }
      try {
        const result = await guestApi.guestJoinOpen(meetingCode, name, password)
        setPasswordPromptOpen(false)
        setPendingJoinName(null)
        if (result.status === "waiting") {
          seedGuestMeeting(result.meeting)
          setWaitingRequestId(result.requestId)
          setWaitingFor(result.waitingFor ?? "admission")
          setScreen("waiting-room")
          return
        }
        admitGuest(result.meeting, result.roomCredentials)
        void enterMeetingWithJoinSound()
      } catch (err: any) {
        if (err?.code === "MEETING_FULL") {
          setPasswordPromptOpen(false)
          setPendingJoinName(null)
          showMeetingFull()
          return
        }
        setPasswordError(
          err?.code === "INVALID_PASSWORD"
            ? WRONG_PASSWORD_MESSAGE
            : err?.message || "Could not join the meeting.",
        )
      }
    },
    [isGuest, meetingCode, pendingJoinName, userName, seedGuestMeeting, admitGuest, enterMeetingWithJoinSound, showMeetingFull],
  )

  const handleCancelPasswordPrompt = useCallback(() => {
    setPasswordPromptOpen(false)
    setPasswordError(null)
    setPendingJoinName(null)
  }, [])

  const handleLeaveMeeting = useCallback((reason: "left" | "ended" | "removed" = "left") => {
    if (leaveTransitionTimerRef.current) return
    emitToParent(reason === "ended" ? "meeting-ended" : "left", { reason })
    playUiSound(LEAVE_SOUND_SRC, 0.9)
    setEndedReason(reason)
    const session = useMeetingSessionStore.getState()
    lastMediaRef.current = session.viewOnly ? null : { micOn: session.micOn, camOn: session.camOn }
    session.clearRoomCredentials()
    // The password was proved for this visit, not kept for the next one.
    clearGuestPassword()
    // Backgrounds are per-session. Leaving drops both, so a rejoin or the next
    // meeting's prejoin starts clean rather than inheriting the last call's.
    useMeetingPreferencesStore.getState().clearBackgrounds()
    useMeetingPreferencesStore.getState().setHiddenSelfVideo(false)
    // The devices are NOT closed here. The meeting UI is still on screen for
    // LEAVE_TRANSITION_DELAY_MS, and its tiles reach for the camera through
    // `getLocalStream()` — so stopping it at this moment just has it reopened a
    // frame later by a tile that is still mounted, and the indicator never goes
    // out. The effect below closes them once the meeting screen has actually
    // gone, which is when nothing is left to ask for them.
    // Same for the personal mix: it is keyed by SFU peerId, which is issued per
    // join, so anything kept here is dead weight that can only ever be matched
    // by a stranger the SFU happens to hand the same id.
    useParticipantVolumeStore.getState().reset()
    useParticipantVideoHiddenStore.getState().reset()
    leaveTransitionTimerRef.current = window.setTimeout(() => {
      setScreen("meeting-ended")
      leaveTransitionTimerRef.current = null
    }, LEAVE_TRANSITION_DELAY_MS)
  }, [playUiSound])

  const handleGoHome = useCallback(() => {
    // Belt and braces alongside the clear in handleLeaveMeeting: this is also
    // reachable from the access-code screen, which never entered a meeting.
    useMeetingPreferencesStore.getState().clearBackgrounds()
    // The dashboard at top level; in a frame, a closed screen and a word to the host.
    exitMeetingRoute(navigate, { reason: "left" })
  }, [navigate])

  // An embedding application hears when we are in, and may ask us to leave or
  // to mute — the same actions as the Leave button and a host's force-mute.
  // All of it is a no-op unless a trusted host frames us (embed-bridge.ts).
  useEffect(() => {
    if (screen !== "meeting") return
    emitToParent("joined", { meetingId: meetingCode })
    const offLeave = onParentCommand("leave", () => handleLeaveMeeting("left"))
    const offMute = onParentCommand("mute", () => useMeetingSessionStore.getState().setMicOn(false))
    return () => {
      offLeave()
      offMute()
    }
  }, [screen, meetingCode, handleLeaveMeeting])

  // Cancel from the waiting room: stop the lobby poll and return to prejoin so
  // the guest can try again (mirrors the old /join "Try again").
  const handleCancelWaiting = useCallback(() => {
    setWaitingRequestId(null)
    setScreen("dashboard")
  }, [])

  const handleWebinarClick = useCallback(() => {
    setCurrentView("webinar")
  }, [])

  const handleWaitingLobbyClick = useCallback(() => {
    setCurrentView("lobby")
  }, [])

  const handleBackToDashboard = useCallback(() => {
    setCurrentView("dashboard")
  }, [])

  // Rejoining is joining. It used to call `enterMeetingWithJoinSound` directly,
  // which only fetches credentials for a signed-in user — a guest was expected
  // to already hold theirs from the admission flow. Leaving clears them, and the
  // admission flow does not run again, so a removed guest pressing "Try to
  // rejoin" re-entered with nothing: no credentials, no connection, and (before
  // the roster fix) a grid of invented people. Going back through the full join
  // path means a guest is re-admitted, a locked room sends them to the waiting
  // room, and a password is asked for again — the same as walking in the door.
  //
  // The one thing it does not take from the door is the mic and camera: the
  // join reads them from the prejoin choice, and there is no prejoin on the way
  // back in, so they are set to how the participant left (see lastMediaRef).
  const handleRejoin = useCallback(() => {
    const last = lastMediaRef.current
    if (last) {
      const devices = useDevicesStore.getState()
      devices.setMicEnabled(last.micOn)
      devices.setCamEnabled(last.camOn)
    }
    // The name they last joined under, not `meetingInfo.userName`: a guest whose
    // credentials were issued before the Join click (guest-join page, the
    // guard) skips the branch that records `joinedName`, so for them that is
    // "" — and with the credentials cleared on leave, the rejoin is a guest
    // admission the server refuses with "name required".
    void handleJoinMeeting(lastJoinRef.current?.name || meetingInfo.userName, meetingCode)
  }, [handleJoinMeeting, meetingInfo.userName, meetingCode])

  // Try again is the same join the Join click makes. The dialog stays up while
  // it runs and closes on any other outcome: in, waiting room, the password
  // prompt, or another error (which says so itself).
  const handleTryAgainFull = useCallback(async () => {
    const last = lastJoinRef.current
    fullAgainRef.current = false
    setFullRetrying(true)
    try {
      await handleJoinMeeting(last?.name ?? meetingInfo.userName, last?.code ?? meetingCode)
    } finally {
      setFullRetrying(false)
    }
    if (fullAgainRef.current) setStillFull(true)
    else setMeetingFull(false)
  }, [handleJoinMeeting, meetingInfo.userName, meetingCode])

  const handleCloseFull = useCallback(() => {
    setMeetingFull(false)
    setFullDismissed(true)
  }, [])

  const handleLeaveFull = useCallback(() => {
    setMeetingFull(false)
    navigate(isGuest ? AUTH_ROUTES.LOGIN : APP_ROUTES.DASHBOARD_HOME, { replace: true })
  }, [isGuest, navigate])

  const handleAccessCodeSubmit = useCallback(() => {
    enterMeetingWithJoinSound()
  }, [enterMeetingWithJoinSound])

  if (screen === "unsupported") {
    return <UnsupportedBrowser reason={unsupportedReason} />
  }

  return (
    <MediaStreamProvider>
      {screen === "dashboard" && currentView === "dashboard" && (
        <div className="animate-in fade-in duration-500">
          <PreJoinPage
            initialUserName={meetingInfo.userName}
            initialMeetingCode={meetingInfo.meetingCode}
            onJoinMeeting={handleJoinMeeting}
            onWebinarClick={handleWebinarClick}
            onWaitingLobbyClick={handleWaitingLobbyClick}
            isWebinar={isWebinar}
            joinBlocked={meetingFull || fullDismissed}
          />
        </div>
      )}

      {currentView === "webinar" && (
        <div className="animate-in fade-in duration-500">
          <WebinarRegistration onBack={handleBackToDashboard} />
        </div>
      )}

      {currentView === "lobby" && (
        <div className="animate-in fade-in duration-500">
          <WaitingLobby
            onBack={handleBackToDashboard}
            userName={meetingInfo.userName}
            meetingCode={meetingInfo.meetingCode}
          />
        </div>
      )}

      {screen === "waiting-room" && (
        <div className="animate-in fade-in duration-700">
          <WaitingRoom
            waitingFor={waitingFor}
            meetingName={meetingInfo.meetingName}
            userName={meetingInfo.userName}
            onCancel={handleCancelWaiting}
          />
        </div>
      )}

      {screen === "access-code" && (
        <div className="animate-in fade-in duration-500">
          <AccessCode
            meetingName={meetingInfo.meetingName}
            onSubmit={handleAccessCodeSubmit}
            onCancel={handleGoHome}
          />
        </div>
      )}

      {screen === "meeting" && (
        <div className="animate-in fade-in duration-700">
          <MeetingScreen meetingInfo={meetingInfo} onLeaveMeeting={handleLeaveMeeting} />
        </div>
      )}

      {passwordPromptOpen && (
        <MeetingPasswordDialog
          open={passwordPromptOpen}
          onOpenChange={(open) => { if (!open) handleCancelPasswordPrompt() }}
          onSubmit={handlePasswordSubmit}
          onCancel={handleCancelPasswordPrompt}
          errorMessage={passwordError ?? undefined}
        />
      )}

      <MeetingFullDialog
        open={meetingFull}
        onClose={handleCloseFull}
        onTryAgain={() => void handleTryAgainFull()}
        onLeave={handleLeaveFull}
        isGuest={isGuest}
        retrying={fullRetrying}
        stillFull={stillFull}
      />

      {screen === "meeting-ended" && (
        <div className="animate-in fade-in duration-500">
          {/* Rejoin on every reason EXCEPT the meeting being over.
              
              Leaving is reversible — the meeting is still running and walking
              back in is the whole point. So is removal: being removed ends
              this session, not the person's access, and a moderator clearing
              someone out of a call they can come back to is intended rather
              than an oversight. Enforcing a ban would need the room to
              remember removals server-side, and deliberately does not.

              An ended meeting is none of those. The host closed it for
              everyone, the server refuses the join, and offering the button
              anyway meant pressing it and being turned away with an error —
              an action shown as available that could not succeed. */}
          <MeetingEnded
            reason={endedReason}
            onRejoin={endedReason === "ended" ? undefined : handleRejoin}
            onGoHome={isEmbedded() ? undefined : handleGoHome}
          />
        </div>
      )}
    </MediaStreamProvider>
  )
}
